import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  BACKGROUND_COLOR_1,
  BAR_LESS_THAN_20_PERCENT,
  BAR_LESS_THAN_70_PERCENT,
  BAR_MORE_THAN_70_PERCENT,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS_1,
  COLOR_TEXT_1,
  DISCOVERY_LIGHT_BACKGROUND_COLOR,
  SIDEBAR_COLOR,
} from 'src/colors';
import discoveryLandingPageImage from 'src/App/assets/discoveryAiLandingPageImage.png';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { callApi } from 'src/core/callApi';
import { LinearProgress } from '@mui/material';
import ScoreCard from '../OverviewPage/UtilityComponents/ScoreCard';
import { COLOR_PRIMARY_DARK } from 'src/const';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import timeLineImage from 'src/App/assets/timeline.png';
import {
  abbreviateNumber,
  convertCurrency,
  trackDiscoveryAIFunnelEvent,
} from 'src/utils/functions';
import { overviewPageCustomTooltipProps } from 'src/interfaces/idashboard';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import LockSharpIcon from '@material-ui/icons/LockSharp';

const MarketInsightsData = {
  'Housing Affordability Index': (38.9 * Math.random()).toFixed(1),
  'Price-to-Rent Ratio': (6.6 * Math.random()).toFixed(1),
  'Days on Market': (70 * Math.random()).toFixed(1),
  'Airbnb Regulation': 'Moderate',
  'Crime Rate': (25.6 * Math.random()).toFixed(1),
  'Home Value Increase (5Y)': (68 * Math.random()).toFixed(1),
  'Population Growth (5Y)': (7.3 * Math.random()).toFixed(1),
  'Household Income Growth (5Y)': (33 * Math.random()).toFixed(1),
};

const BarGraphData = [
  {
    name: 'Y1',
    totalProfit: 9500,
    propertyPrice: 47400,
  },
  {
    name: 'Y3',
    totalProfit: 30400,
    propertyPrice: 54000,
  },
  {
    name: 'Y5',
    totalProfit: 54200,
    propertyPrice: 61400,
  },
  {
    name: 'Y10',
    totalProfit: 129000,
    propertyPrice: 85000,
  },
];

const DummyMarketData = {
  admin_geoid: '22500',
  adr: 110,
  annual_appreciation: 5.5,
  average_expense: 10084,
  center: 'POINT(-80.082997 34.367881)',
  city: 'SC',
  country: 'United States',
  country_code: 'US',
  district: 'Hartsville',
  geo_type: null,
  income_change: 28.2,
  income_change_diff_with_nat_avg: 21,
  income_change_rank: 1572,
  net_rental_yield: 144.1,
  occupancy_rate: 59,
  population_change: -2.9,
  population_change_diff_with_nat_avg: -193.5,
  population_change_rank: 2164,
  priority: 'B',
  prop_price_change_diff_with_nat_avg: -26.7,
  property_price: 10500,
  property_price_change: 33.5,
  property_price_change_rank: 1699,
  revenue: 25210,
  ten_years_roi: 17.8,
  ten_years_wealth_table: {
    year_1: { property_price: 10500, total_profit: 15130, total_wealth: 25630 },
    year_2: { property_price: 11078, total_profit: 31093, total_wealth: 42171 },
    year_3: { property_price: 11687, total_profit: 47934, total_wealth: 59621 },
    year_4: { property_price: 12330, total_profit: 65701, total_wealth: 78030 },
    year_5: { property_price: 13008, total_profit: 84445, total_wealth: 97452 },
    year_6: { property_price: 13723, total_profit: 104220, total_wealth: 117943 },
    year_7: { property_price: 14478, total_profit: 125082, total_wealth: 139560 },
    year_8: { property_price: 15274, total_profit: 147092, total_wealth: 162366 },
    year_9: { property_price: 16114, total_profit: 170313, total_wealth: 186427 },
    year_10: { property_price: 17000, total_profit: 194811, total_wealth: 211811 },
  },
  total_supply: 17,
  isDummy: true,
};

const DiscoveryAi = ({ handleOnSampleClick }) => {
  const [selectedMarketName, setSelectedMarketName] = useState<string>('Hartsville, SC');
  const [selectedTab, setSelectedTab] = useState<string>('insights');
  const [markets, setMarkets] = useState<any[]>([]);
  const [randomValue, setRandomValue] = useState<number>(Math.random());
  const [marketInsightsDetailedData, setMarketInsightsDetailedData] = useState<any>(
    MarketInsightsData
  );
  const [searchBarText, setSearchBarText] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');
  const [showProgressBar, setShowProgressBar] = useState<boolean>(false);
  const [selectedMarketInDiscoveryAI, setSelectedMarketInDiscoveryAI] = useState<string>('');
  const [isDataNotAvailable, setIsDataNotAvailable] = useState<boolean>(false);
  const [marketSortingKey, setMarketSortingKey] = useState<string>('relevance');
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const isMobile = useMediaQuery(theme.breakpoints.down(520));
  // console.log(">>>>>>>>isDataNotAvailable",isDataNotAvailable)

  useEffect(() => {
    trackDiscoveryAIFunnelEvent('landedOnDiscoveryAiTool');
  }, []);

  useEffect(() => {
    if (selectedMarketInDiscoveryAI) {
      setSelectedMarketName(selectedMarketInDiscoveryAI);
      const Data = {
        'Housing Affordability Index': (38.9 * Math.random()).toFixed(1),
        'Price-to-Rent Ratio': (6.6 * Math.random()).toFixed(1),
        'Days on Market': (70 * Math.random()).toFixed(1),
        'Airbnb Regulation': 'Moderate',
        'Crime Rate': (25.6 * Math.random()).toFixed(1),
        'Home Value Increase (5Y)': (68 * Math.random()).toFixed(1),
        'Population Growth (5Y)': (7.3 * Math.random()).toFixed(1),
        'Household Income Growth (5Y)': (33 * Math.random()).toFixed(1),
      };
      setMarketInsightsDetailedData(Data);
    }
  }, [selectedMarketInDiscoveryAI]);

  useEffect(() => {
    const fetchData = async () => {
      let retryCount = 0;
      while (retryCount < 2) {
        try {
          setMarkets([]);
          setShowProgressBar(true);
          setIsDataNotAvailable(false);
          const URL =
            'https://7jpzhby7e1.execute-api.us-east-2.amazonaws.com/default/discoverMarketsWithAI';
          const config = {
            queryParams: {
              userPrompt: prompt,
            },
          };

          let res = await callApi(URL, config, true);

          // console.log('>>>RES', res.message.markets);
          let markets: any = [];
          for (let data of res.message.markets) {
            markets.push(data);
          }

          for (let i = 0; i < 6 - res.message.markets.length; i++) {
            if (res.message.markets[0]) {
              DummyMarketData.country_code = res.message.markets[0].country_code;
              markets.push(DummyMarketData);
            }
          }

          setMarkets(markets);
          setShowProgressBar(false);
          if (res.message.markets.length == 0) {
            setIsDataNotAvailable(true);
          } else {
            setSelectedMarketName(res.message.markets[0].district);
          }
          break;
        } catch (error) {
          setShowProgressBar(false);
          retryCount += 1;
        }
      }
    };

    if (prompt) {
      fetchData();
    }
  }, [prompt]);

  useEffect(() => {
    console.log('>>>markets', markets);
  }, [markets]);

  const marketInsightsbarColorAndText = (barName, value) => {
    if (barName === 'Housing Affordability Index') {
      if (value <= 20) {
        return [BAR_LESS_THAN_20_PERCENT, ': Less Affordability', value, barName];
      } else if (value > 20 && value < 70) {
        return [BAR_LESS_THAN_70_PERCENT, ': Limited Affordability', value, barName];
      } else {
        return [BAR_MORE_THAN_70_PERCENT, ': High Affordability', value, barName];
      }
    } else if (barName === 'Price-to-Rent Ratio') {
      if (value <= 2) {
        return [BAR_LESS_THAN_20_PERCENT, ': Less Rental Demand', value * 10, barName];
      } else if (value > 2 && value < 7) {
        return [BAR_LESS_THAN_70_PERCENT, ': Moderate Rental Demand', value * 10, barName];
      } else {
        return [BAR_MORE_THAN_70_PERCENT, ': High Rental Demand', value * 10, barName];
      }
    } else if (barName === 'Days on Market') {
      if (value <= 100) {
        return [BAR_MORE_THAN_70_PERCENT, ': Hot Market', value, barName];
      } else if (value > 100 && value < 300) {
        return [BAR_LESS_THAN_70_PERCENT, ': Average Market', value, barName];
      } else {
        return [BAR_LESS_THAN_20_PERCENT, ': Bad Market', value, barName];
      }
    } else if (barName === 'Airbnb Regulation') {
      if (value == 'low') {
        return [BAR_MORE_THAN_70_PERCENT, ' ', value, barName];
      } else if (value == 'Moderate') {
        return [BAR_LESS_THAN_70_PERCENT, ' ', value, barName];
      } else {
        return [BAR_LESS_THAN_20_PERCENT, ' ', value, barName];
      }
    } else if (barName === 'Crime Rate') {
      if (value <= 5) {
        return [BAR_MORE_THAN_70_PERCENT, '% per 1,000 residents: Very Less', value, barName];
      } else if (value > 5 && value < 15) {
        return [BAR_LESS_THAN_70_PERCENT, '% per 1,000 residents: Moderate', value, barName];
      } else {
        return [BAR_LESS_THAN_20_PERCENT, '% per 1,000 residents: High', value, barName];
      }
    } else if (barName === 'Home Value Increase (5Y)') {
      if (value <= 20) {
        return [BAR_LESS_THAN_20_PERCENT, ': Low', value, barName];
      } else if (value > 20 && value < 50) {
        return [BAR_LESS_THAN_70_PERCENT, ': Moderate', value, barName];
      } else {
        return [BAR_MORE_THAN_70_PERCENT, ': High', value, barName];
      }
    } else if (barName === 'Population Growth (5Y)') {
      if (value <= 2) {
        return [BAR_LESS_THAN_20_PERCENT, ': Low', value * 10, barName];
      } else if (value > 2 && value < 7) {
        return [BAR_LESS_THAN_70_PERCENT, ': Medium', value * 10, barName];
      } else {
        return [BAR_MORE_THAN_70_PERCENT, ': High', value * 10, barName];
      }
    } else {
      if (value <= 20) {
        return [BAR_LESS_THAN_20_PERCENT, ': Low', value, barName];
      } else if (value > 20 && value < 70) {
        return [BAR_LESS_THAN_70_PERCENT, ': Moderate', value, barName];
      } else {
        return [BAR_MORE_THAN_70_PERCENT, ': High', value, barName];
      }
    }
  };

  const getTooltipText = key => {
    if (key === 'Housing Affordability Index') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Housing Affordability Index</span> :{' '}
          <span style={{ filter: 'blur(3.5px)' }}>Limited</span> affordability for median-income
          families.<span style={{ filter: 'blur(3.5px)' }}>61.4% lower</span> than National Typical
          of <span style={{ filter: 'blur(3.5px)' }}>103</span>.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Affordability has{' '}
          <span style={{ filter: 'blur(3.5px)' }}>improved</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>20</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else if (key === 'Price-to-Rent Ratio') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Price-to-Rent Ratio</span> : The cost of purchasing a
          property is <span style={{ filter: 'blur(3.5px)' }}>6.6</span> times the annual cost of
          renting a similar property. Buying may be relatively more affordable compared to renting.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : PTR has{' '}
          <span style={{ filter: 'blur(3.5px)' }}>improved</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>20</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else if (key === 'Days on Market') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Days on Market</span> : On average, it takes{' '}
          <span style={{ filter: 'blur(3.5px)' }}>70</span> days for a property to sell after being
          listed. <span style={{ filter: 'blur(3.5px)' }}>42</span> days{' '}
          <span style={{ filter: 'blur(3.5px)' }}>more</span> than the national median.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Days on Market has{' '}
          <span style={{ filter: 'blur(3.5px)' }}>reduced</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>96</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else if (key === 'Crime Rate') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Crime Rate</span> : Only{' '}
          <span style={{ filter: 'blur(3.5px)' }}>15</span> % of cities in the country have higher
          crime rate than this market.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Crime Rate has{' '}
          <span style={{ filter: 'blur(3.5px)' }}>improved</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>20</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else if (key === 'Home Value Increase (5Y)') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Home Value Increase (5Y)</span> :{' '}
          <span style={{ filter: 'blur(3.5px)' }}>12</span> % more than the national median.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Median 1 bedroom property prices
          has <span style={{ filter: 'blur(3.5px)' }}>increased</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>68</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else if (key === 'Population Growth (5Y)') {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Population Growth (5Y)</span> :{' '}
          <span style={{ filter: 'blur(3.5px)' }}>12</span> % more than the national median.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Population has{' '}
          <span style={{ filter: 'blur(3.5px)' }}>increased</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>68</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    } else {
      return [
        <span>
          <span style={{ fontWeight: 500 }}>Household Income Growth (5Y)</span> :{' '}
          <span style={{ filter: 'blur(3.5px)' }}>12</span> % more than the national median.
        </span>,
        <span>
          <span style={{ fontWeight: 500 }}>5-year Trend</span> : Median household income has
          <span style={{ filter: 'blur(3.5px)' }}>increased</span> by{' '}
          <span style={{ filter: 'blur(3.5px)' }}>33</span> % in 5 years between 2018 and 2023.
        </span>,
      ];
    }
  };

  const CustomLegend = props => {
    const { payload } = props;
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, marginRight: '10px' }}>
          <Box
            sx={{
              height: '10px',
              width: '10px',
              borderRadius: '10px',
              backgroundColor: COLOR_SUCCESS_1,
            }}
          />
          <Typography sx={{ fontSize: '10px' }}>Total Profit </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box
            sx={{ height: '10px', width: '10px', borderRadius: '10px', backgroundColor: '#FFA500' }}
          />
          <Typography sx={{ fontSize: '10px' }}>Property Price</Typography>
        </Box>
      </Box>
    );
  };

  const CustomTooltip: React.FC<overviewPageCustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const keyNames = {
        totalProfit: 'Total Profit: $',
        propertyPrice: 'Property Price: $',
      };

      return (
        <Box
          sx={{
            height: '90px',
            width: '180px',
            backgroundColor: DISCOVERY_LIGHT_BACKGROUND_COLOR,
            borderRadius: '10px',
            boxShadow: '0 4px 10px #0003',
            display: 'flex',
            flexDirection: 'column',
            padding: '10px',
            gap: '5px',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              backgroundColor: SIDEBAR_COLOR,
              borderRadius: '10px',
              marginBottom: '5px',
              padding: '10px',
            }}
          >
            {keyNames[payload[0]['dataKey']]} :{' '}
            <span
              style={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
              onClick={handleOnSampleClick}
            >
              {payload[0]['value']}
            </span>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: SIDEBAR_COLOR,
              borderRadius: '10px',
              marginBottom: '5px',
              padding: '10px',
            }}
          >
            {keyNames[payload[1]['dataKey']]} :{' '}
            <span
              style={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
              onClick={handleOnSampleClick}
            >
              {payload[1]['value']}
            </span>
          </Typography>
        </Box>
      );
    }

    return null;
  };

  const CustomToolTipCard = ({ keyProp }) => {
    trackDiscoveryAIFunnelEvent('checkedMarketInsightsTooltip');
    let textData = getTooltipText(keyProp);
    return keyProp !== 'Airbnb Regulation' ? (
      <Box
        sx={{ width: '100%', backgroundColor: DISCOVERY_LIGHT_BACKGROUND_COLOR, padding: '10px' }}
      >
        <Typography
          variant="body2"
          sx={{ color: COLOR_TEXT_1, backgroundColor: SIDEBAR_COLOR, padding: '5px' }}
        >
          {textData[0]}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: COLOR_TEXT_1,
            backgroundColor: SIDEBAR_COLOR,
            padding: '5px',
            marginTop: '5px',
          }}
        >
          {textData[1]}
        </Typography>
      </Box>
    ) : null; // Use null for no rendering when condition is false
  };

  const handleTabChange = (event, newValue) => {
    trackDiscoveryAIFunnelEvent(`clicked${newValue}Tab`);
    if (newValue === 'trends' || newValue === 'climate') handleOnSampleClick();
    else setSelectedTab(newValue);
  };

  return (
    <Grid
      container
      xs={12}
      sx={{ width: '100%', backgroundColor: SIDEBAR_COLOR, marginTop: '40px' }}
    >
      {/* Hero Section */}
      <Grid item container xs={12} sx={{ height: '500px' }}>
        <Grid container item xs={matches ? 12 : 8} sx={{ padding: matches ? '20px' : '50px 30px' }}>
          <Grid item xs={12}>
            <Typography variant="h4" sx={{ color: COLOR_PRIMARY_2, fontWeight: 1000 }}>
              Find Short-term Rental Markets tailored to Your Lifestyle and Investment Goals
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: matches ? '10px' : '0px' }}>
            <Typography variant="h5" sx={{ color: COLOR_TEXT_1 + 46 }}>
              Enter your lifestyle preferences and investment goals. Let AI uncover short-term
              rental markets tailored to you.
            </Typography>
          </Grid>
          <Grid
            container
            item
            xs={12}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Box
              sx={{
                backgroundColor: SIDEBAR_COLOR,
                width: '100%',
                height: '60px',
                borderRadius: '30px',
                boxShadow: '4px 4px 10px 2px #3AAFA9',
                display: 'flex',
                alignItems: 'center',
                padding: '0px 15px',
                marginTop: matches ? '10px' : '0px',
              }}
            >
              <Box sx={{ width: '5%' }}>
                <AutoAwesomeIcon sx={{ color: COLOR_PRIMARY_1 }} />
              </Box>
              <Box sx={{ width: '75%' }}>
                <TextField
                  fullWidth
                  id="fullWidth"
                  autoFocus={true}
                  placeholder="Ski resorts near Denver with properties under $100k and high Airbnb returns."
                  onChange={e => {
                    setSearchBarText(e.target.value);
                  }}
                  sx={{ '.MuiOutlinedInput-notchedOutline': { borderStyle: 'none' } }}
                />
              </Box>
              <Box sx={{ width: '20%' }}>
                <Button
                  sx={{
                    backgroundColor: COLOR_PRIMARY_1,
                    color: SIDEBAR_COLOR,
                    borderRadius: '30px',
                    width: '100%',
                    '&:hover': {
                      backgroundColor: COLOR_PRIMARY_1,
                      color: SIDEBAR_COLOR,
                    },
                  }}
                  onClick={() => {
                    setPrompt(searchBarText);
                    trackDiscoveryAIFunnelEvent('searchedPrompt');
                  }}
                >
                  {showProgressBar ? (
                    <CircularProgress size={30} sx={{ color: SIDEBAR_COLOR }} />
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: matches ? '12px' : '18px',
                      }}
                    >
                      Discover
                      {!matches && <ArrowForwardIcon sx={{ marginLeft: '5px' }} />}
                    </Typography>
                  )}
                </Button>
              </Box>
            </Box>
          </Grid>
          {isDataNotAvailable && (
            <Typography variant="h6" sx={{ marginTop: '20px' }}>
              Sorry, we couldn't find any markets related to your search. Could you please try
              something more specific?
            </Typography>
          )}
        </Grid>
        {!matches && (
          <Grid item xs={4} sx={{ padding: '20px' }}>
            <img
              src={discoveryLandingPageImage}
              loading="lazy"
              alt="Discovery Ai Page Image"
              style={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
                borderRadius: '20px',
                boxShadow: '0px 4px 10px 2px #3AAFA9',
              }}
            />
          </Grid>
        )}
      </Grid>

      {markets.length > 0 && (
        <Grid item container xs={12} sx={{ marginTop: '20px', padding: matches ? '20px' : '0px' }}>
          <Typography variant="body1" sx={{ color: COLOR_PRIMARY_2, marginBottom: 2 }}>
            Good news! We've found 50+ relevant areas for you! With an average short-term rental
            yield of 14.5% and predicted annual appreciation of 4.5%, find markets to maximize your
            potential returns 🚀. Explore now!
          </Typography>
          <Typography variant="body1" sx={{ color: COLOR_PRIMARY_2 }}>
            Our annual appreciation is estimated based on the past 5 years' historical price trend,
            population, and income growth.{' '}
            <span
              style={{ color: COLOR_PRIMARY_1, textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => {
                trackDiscoveryAIFunnelEvent('clickedLearnMore');
              }}
            >
              Learn More
            </span>
          </Typography>
        </Grid>
      )}

      {/* Search result */}
      {markets.length > 0 && (
        <Grid item container xs={12} sx={{ marginTop: '20px', padding: matches ? '20px' : '0px' }}>
          <Grid container item xs={12} sx={{ maxHeight: '50px' }}>
            <Grid
              item
              xs={matches ? 12 : 6}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Box sx={{ marginLeft: '10px' }}>
                <FormControl>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={marketSortingKey}
                    label=""
                    onChange={e => {
                      handleOnSampleClick();
                    }}
                    sx={{
                      '& .MuiSelect-select': {
                        padding: '4px 0px 4px 10px',
                      },
                      marginRight: '10px',
                    }}
                  >
                    <MenuItem value={'relevance'}>Relevance</MenuItem>
                    <MenuItem value={'annualAppreciation'}>
                      Annual Appreciation{' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                    <MenuItem value={'rentalYield'}>
                      Net Rental Yield{' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                    <MenuItem value={'marketDays'}>
                      Days on Market{' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                    <MenuItem value={'homeValueIncrease'}>
                      Home Value Increase (5Y){' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                    <MenuItem value={'populationGrowth'}>
                      Population Growth (5Y){' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                    <MenuItem value={'houseHoldIncomeGrowth'}>
                      Household Income Growth (5Y){' '}
                      <LockSharpIcon
                        style={{ fontSize: 18, color: 'inherit', marginLeft: '5px' }}
                      />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={matches ? 12 : 6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                sx={{
                  color: SIDEBAR_COLOR,
                  backgroundColor: COLOR_PRIMARY_1,
                  borderRadius: '10px',
                  paddingX: '20px',
                  '&:hover': {
                    backgroundColor: COLOR_PRIMARY_1,
                  },
                }}
                onClick={() => {
                  handleOnSampleClick();
                  trackDiscoveryAIFunnelEvent('clickedUnlockButtonExplicitly');
                }}
              >
                Unlock All Results
              </Button>
            </Grid>
          </Grid>
          <Grid container item xs={12} sx={{ marginTop: '20px' }}>
            {/* Card View */}
            <Grid container item xs={matches ? 12 : 6} sx={{ marginTop: matches ? '30px' : '0px' }}>
              {markets &&
                markets.map((market, index) => (
                  <Grid item key={index}>
                    <ScoreCard
                      district={market.district}
                      countryCode={market.country_code}
                      country={market.country}
                      score={
                        market.ten_years_roi ? market.ten_years_roi : Math.floor(randomValue * 21)
                      }
                      propertyPrice={
                        market.property_price
                          ? market.property_price
                          : Math.floor(randomValue * 111259)
                      }
                      propertyPriceAppreciation={
                        market.property_price_change
                          ? market.property_price_change
                          : Math.floor(randomValue * 846294)
                      }
                      populationGrowth={
                        market.population_change
                          ? market.population_change
                          : Math.floor(randomValue * 10)
                      }
                      medianHouseholdIncomeGrowth={
                        market.income_change ? market.income_change : Math.floor(randomValue * 10)
                      }
                      grossRentalYield={
                        market.net_rental_yield
                          ? market.net_rental_yield
                          : Math.floor(randomValue * 10)
                      }
                      city={market.city}
                      market={market}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                      marketsToCompareList={[]}
                      setMarketsToCompareList={[]}
                      minROI={5}
                      maxROI={0}
                      revenue={market.revenue ? market.revenue : Math.floor(randomValue * 523654)}
                      currency={'USD'}
                      annualAppreciation={
                        market.annual_appreciation
                          ? market.annual_appreciation
                          : Math.floor(randomValue * 10)
                      }
                      isMarketDataAccessible={false}
                      freeMarketDataKey={[]}
                      matches={matches}
                      handleOnSampleClick={handleOnSampleClick}
                      marketGeoType={market.geo_type ? market.geo_type : null}
                      marketLength={0}
                      isFromDiscoveryAi={true}
                      setSelectedMarketInDiscoveryAI={setSelectedMarketInDiscoveryAI}
                    />
                  </Grid>
                ))}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                <Pagination
                  count={10}
                  page={1}
                  onClick={handleOnSampleClick}
                  variant="outlined"
                  sx={{ '.Mui-selected': { color: COLOR_PRIMARY_2 } }}
                />
              </Grid>
            </Grid>
            {/* Detailed view */}
            <Grid
              container
              item
              xs={matches ? 12 : 6}
              sx={{ marginTop: matches ? '30px' : '0px' }}
              id="detailedAIView"
            >
              <Grid
                item
                xs={12}
                sx={{
                  boxShadow: '0px 4px 4px 0px #00000040',
                  padding: '10px',
                }}
              >
                <Grid item xs={12} sx={{ maxHeight: '30px' }}>
                  <Typography variant="h6" sx={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}>
                    {selectedMarketName}
                  </Typography>
                </Grid>
                <Grid container item xs={12}>
                  <TabContext value={selectedTab}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
                      <Tabs onChange={handleTabChange} value={selectedTab} sx={{ width: '100%' }}>
                        <Tab
                          label={<Typography sx={{ display: 'flex' }}>Market Insights</Typography>}
                          value="insights"
                          sx={{ '& .MuiTab-root.Mui-selected': { color: COLOR_PRIMARY_2 } }}
                        />
                        <Tab
                          label={<Typography sx={{ display: 'flex' }}>Return Metrics</Typography>}
                          value="metrics"
                          sx={{ '& .MuiTab-root.Mui-selected': { color: COLOR_PRIMARY_2 } }}
                        />
                        <Tab
                          label={
                            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                              Travel Trends <LockIcon sx={{ fontSize: '20px' }} />
                            </Typography>
                          }
                          value="trends"
                          sx={{ '& .MuiTab-root.Mui-selected': { color: COLOR_PRIMARY_2 } }}
                        />
                        <Tab
                          label={
                            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                              Climate <LockIcon sx={{ fontSize: '20px' }} />
                            </Typography>
                          }
                          value="climate"
                          sx={{ '& .MuiTab-root.Mui-selected': { color: COLOR_PRIMARY_2 } }}
                        />
                      </Tabs>
                    </Box>
                    <TabPanel value="insights" style={{ padding: 0 }}>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          marginTop: '5px',
                          backgroundColor: DISCOVERY_LIGHT_BACKGROUND_COLOR,
                          borderRadius: '10px',
                          padding: '10px',
                        }}
                      >
                        {Object.entries(marketInsightsDetailedData).map(([key, value]) => {
                          const [
                            color,
                            text,
                            FormattedValue,
                            barName,
                          ] = marketInsightsbarColorAndText(key, value);
                          return (
                            <Grid
                              container
                              key={key}
                              sx={{
                                height: matches ? '70px' : '50px',
                                borderRadius: '10px',
                                backgroundColor: SIDEBAR_COLOR,
                                marginBottom: '10px',
                              }}
                            >
                              <Grid
                                item
                                xs={6}
                                sx={{ display: 'flex', alignItems: 'center', padding: '5px' }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: BACKGROUND_COLOR_1,
                                    fontWeight: 500,
                                  }}
                                >
                                  {key}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sx={{ padding: '5px' }}>
                                <InfoTooltip title={<CustomToolTipCard keyProp={barName} />}>
                                  <Box
                                    sx={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
                                    onClick={handleOnSampleClick}
                                  >
                                    <Typography variant="body1">
                                      {value}
                                      {text}
                                    </Typography>
                                    <Box
                                      sx={{
                                        width: '100%',
                                        overflow: 'hidden',
                                        borderRadius: '5px',
                                        height: '10px',
                                        backgroundColor: DISCOVERY_LIGHT_BACKGROUND_COLOR,
                                      }}
                                    >
                                      {key !== 'Airbnb Regulation' ? (
                                        <Box
                                          sx={{
                                            height: '100%',
                                            width: `${FormattedValue}%`,
                                            backgroundColor: color,
                                          }}
                                        />
                                      ) : (
                                        <Box
                                          sx={{
                                            height: '100%',
                                            width: `50%`,
                                            backgroundColor: color,
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </InfoTooltip>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </TabPanel>
                    <TabPanel value="metrics" style={{ padding: 0, width: '100%' }}>
                      <Grid
                        container
                        item
                        xs={12}
                        sx={{
                          marginTop: '5px',
                          backgroundColor: DISCOVERY_LIGHT_BACKGROUND_COLOR,
                          borderRadius: '10px',
                          padding: '10px',
                          height: '550px',
                        }}
                      >
                        <Grid container item xs={12}>
                          <Grid item xs={3}>
                            <Box
                              sx={{
                                height: '70px',
                                borderRadius: '10px',
                                backgroundColor: SIDEBAR_COLOR,
                                padding: '5px',
                                margin: '5px',
                              }}
                            >
                              <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                                Property Price (1bd)
                              </Typography>
                              <Typography
                                variant={matches ? 'body1' : 'h5'}
                                sx={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}
                              >
                                $
                                <span
                                  style={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
                                  onClick={handleOnSampleClick}
                                >
                                  156
                                </span>{' '}
                                K
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={3}>
                            <Box
                              sx={{
                                height: '70px',
                                borderRadius: '10px',
                                backgroundColor: SIDEBAR_COLOR,
                                padding: '5px',
                                margin: '5px',
                              }}
                            >
                              <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                                Annual Revenue (1bd)
                              </Typography>
                              <Typography
                                variant={matches ? 'body1' : 'h5'}
                                sx={{ color: COLOR_PRIMARY_2, fontWeight: 500 }}
                              >
                                $
                                <span
                                  style={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
                                  onClick={handleOnSampleClick}
                                >
                                  32
                                </span>{' '}
                                K
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={3}>
                            <Box
                              sx={{
                                height: '70px',
                                borderRadius: '10px',
                                backgroundColor: SIDEBAR_COLOR,
                                padding: '5px',
                                margin: '5px',
                              }}
                            >
                              <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                                Seasonality
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                }}
                                onClick={handleOnSampleClick}
                              >
                                <LockIcon sx={{ color: COLOR_PRIMARY_1 }} />
                                <img
                                  src={timeLineImage}
                                  loading="lazy"
                                  alt="TimeLine image"
                                  style={{
                                    height: '20px',
                                    width: '90%',
                                  }}
                                />
                              </Box>
                            </Box>
                          </Grid>
                          <Grid item xs={3}>
                            <Box
                              sx={{
                                height: '70px',
                                borderRadius: '10px',
                                backgroundColor: SIDEBAR_COLOR,
                                padding: '5px',
                                margin: '5px',
                              }}
                            >
                              <Typography sx={{ fontWeight: 500, fontSize: '12px' }}>
                                Net Rental Yield
                              </Typography>
                              <Typography
                                variant={matches ? 'body2' : 'h6'}
                                sx={{ color: COLOR_PRIMARY_2 }}
                              >
                                <span
                                  style={{ filter: 'blur(3.5px)', cursor: 'pointer' }}
                                  onClick={handleOnSampleClick}
                                >
                                  48
                                </span>{' '}
                                %
                              </Typography>
                              <Typography sx={{ fontSize: '12px' }}>
                                <span
                                  style={{
                                    filter: 'blur(3.5px)',
                                    color: COLOR_PRIMARY_2,
                                    cursor: 'pointer',
                                  }}
                                  onClick={handleOnSampleClick}
                                >
                                  4.96
                                </span>{' '}
                                than nation median
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Grid container item xs={12}>
                          <Box
                            sx={{
                              width: '100%',
                              maxHeight: '360px',
                              borderRadius: '10px',
                              backgroundColor: SIDEBAR_COLOR,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '10px',
                              }}
                            >
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                Investment Value Over Time
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                Annual Appreciation:
                                <span
                                  style={{
                                    filter: 'blur(3.5px)',
                                    color: COLOR_PRIMARY_2,
                                    cursor: 'pointer',
                                  }}
                                  onClick={handleOnSampleClick}
                                >
                                  56
                                </span>
                                %
                              </Typography>
                            </Box>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                width={500}
                                height={300}
                                data={BarGraphData}
                                margin={{
                                  top: 20,
                                  right: 30,
                                  left: 20,
                                  bottom: 5,
                                }}
                              >
                                <XAxis
                                  dataKey="name"
                                  style={{
                                    fontSize: '0.75rem',
                                    fontFamily: 'Roboto',
                                  }}
                                />
                                <YAxis
                                  tickLine={false}
                                  axisLine={true}
                                  tickFormatter={props => '$' + abbreviateNumber(props)}
                                  style={{
                                    fontSize: '14px',
                                    fontFamily: 'Roboto',
                                    filter: 'blur(3.5px)',
                                    cursor: 'pointer',
                                  }}
                                  onClick={handleOnSampleClick}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend content={<CustomLegend />} />
                                <Bar dataKey="totalProfit" stackId="a" fill={COLOR_SUCCESS_1} />
                                <Bar
                                  dataKey="propertyPrice"
                                  stackId="a"
                                  fill="#FFA500"
                                  radius={[10, 10, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </Box>
                        </Grid>
                      </Grid>
                    </TabPanel>
                    <TabPanel value="trends">Travel Trends</TabPanel>
                    <TabPanel value="climate">climate</TabPanel>
                  </TabContext>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {showProgressBar && (
        <Grid item xs={12} sx={{ width: '100%', marginBottom: 20, color: COLOR_PRIMARY_DARK }}>
          <LinearProgress
            sx={{
              '.MuiLinearProgress-root': {
                height: '10px',
              },
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default DiscoveryAi;
